import React, { useState } from 'react';
// Import the hook for memory monitoring
import useMemoryMonitor from '@/utils/hooks/useMemoryMonitor';
import './index.scss';

interface MemoryMonitorProps {
  showUI?: boolean;
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  interval?: number;
  onlyDevelopment?: boolean;
}

const MemoryMonitorComponent: React.FC<MemoryMonitorProps> = ({
  showUI = true,
  position = 'bottom-right',
  interval = 5000,
  onlyDevelopment = true,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Check if we should show the monitor based on URL parameter or environment
  const shouldShowMonitor = () => {
    // Always show in development mode
    if (process.env.NODE_ENV === 'development') return true;

    // In production, check for URL parameter
    if (onlyDevelopment) {
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.has('showMemoryMonitor');
    }

    return true; // If onlyDevelopment is false, always show
  };

  if (!shouldShowMonitor()) {
    return null;
  }

  // Use the memory monitor hook
  const { memoryInfo, isSupported } = useMemoryMonitor({
    interval,
    enabled: true,
  });

  // Don't render if not supported or no data available
  if (!isSupported) {
    return null;
  }

  if (!showUI || !memoryInfo) {
    return null;
  }

  // Determine color based on usage percentage
  const getColorClass = (percentage: number) => {
    if (percentage >= 90) return 'critical';
    if (percentage >= 70) return 'warning';
    return 'normal';
  };

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const formatBytes = (bytes: number) => {
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`;
  };

  const colorClass = getColorClass(memoryInfo.usagePercentage);

  return (
    <div
      className={`memory-monitor ${position} ${isExpanded ? 'expanded' : 'collapsed'}`}
    >
      <div className='memory-monitor-header' onClick={toggleExpand}>
        <span className='memory-monitor-title'>Memory</span>
        <span className={`memory-monitor-percentage ${colorClass}`}>
          {memoryInfo.usagePercentage.toFixed(1)}%
        </span>
      </div>

      {isExpanded && (
        <div className='memory-monitor-details'>
          <div className='memory-monitor-detail'>
            <span>Used:</span>
            <span>{formatBytes(memoryInfo.usedJSHeapSize)}</span>
          </div>
          <div className='memory-monitor-detail'>
            <span>Total:</span>
            <span>{formatBytes(memoryInfo.totalJSHeapSize)}</span>
          </div>
          <div className='memory-monitor-detail'>
            <span>Limit:</span>
            <span>{formatBytes(memoryInfo.jsHeapSizeLimit)}</span>
          </div>
          <div className='memory-monitor-progress'>
            <div
              className={`memory-monitor-progress-bar ${colorClass}`}
              style={{ width: `${memoryInfo.usagePercentage}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default MemoryMonitorComponent;
